import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

import { ModalActionSource } from '@root/src/redux/modal/modal-state.interface';
import BoonButton from '../button/boon-button';
import BoonLine from '../button/boon-line';

interface RegisterFormProps {
    onSubmit: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
    onClickSubmit: (event) => void;
    actionSource?: ModalActionSource;
}

interface FormData {
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
}

const RegisterForm: React.FC<RegisterFormProps> = props => {
    const { t } = useTranslation();
    const { register, handleSubmit, errors, watch } = useForm<FormData>();
    const [showPassword, setPasswordShow] = useState(true);
    const [eye, setEye] = useState(true);
    const [disabled, setDisabled] = useState(false);

    const onSubmit = handleSubmit(async ({ email, password, firstName, lastName }: FormData) => {
        setDisabled(true);
        await props.onSubmit(email, password, firstName, lastName);
        setDisabled(false);
    });

    const rebuildTooltip = () => {
        setTimeout(() => {
            ReactTooltip.rebuild();
        }, 10);
    };

    const onClickSubmit = event => {
        event.preventDefault();
        props.onClickSubmit(event);
        onSubmit();
    };

    const onClickEye = () => {
        if (showPassword === true) {
            setPasswordShow(false);
            setEye(false);
            return;
        }
        setPasswordShow(true);
        setEye(true);
    };

    return (
        <form name="signup-form" className="pt-1 px-5 pb-0 rounded text-charcoal" onSubmit={onSubmit} noValidate>
            <ReactTooltip
                id="input"
                className="w-1/2 xsm:w-4/12 md:w-3/12 lg:w-2/12 xxl:w-1/12 text-xs"
                effect="solid"
                offset={{ top: -15 }}
                event="focusin"
                eventOff="focusout"
                delayShow={250}
            />
            <ReactTooltip
                id="validation"
                className="w-1/2 md:w-4/12 lg:w-2/12 xxl:w-1/12 text-xs"
                effect="solid"
                event="focusin"
                eventOff="blur"
                multiline={true}
                place="left"
                delayShow={250}
            />
            <div className="text-2xl font-gr-book font-normal text-center text-charcoal mb-4 leading-snug">
                {t('base:signup-modal-title')}
                <div className="signupCaption text-sm leading-tight w-11/12 mx-auto mt-1">
                    {props.actionSource === ModalActionSource.StyleQuiz
                        ? t('base:signup-modal-stylequiz-subtitle')
                        : t('base:signup-modal-subtitle')}
                </div>
            </div>

            <div className="relative mb-4">
                <div className="h-full border-t border-r border-mini-boon border-solid">
                    <div className="EmailInput relative border-b border-clay">
                        <label
                            htmlFor="email"
                            className="label leading-tighter text-black font-gr-book text-xs cursor-text inline-block mt-2"
                        >
                            {t('base:email-label')}
                        </label>
                        <input
                            className="input text-xs appearance-none w-full px-2 py-2 pb-1 focus focus:border-indigo-600 focus:outline-none active:outline-none active:border-indigo-600"
                            id="email-register"
                            type="email"
                            name="email"
                            onFocus={rebuildTooltip}
                            onChange={rebuildTooltip}
                            autoFocus
                            ref={register({
                                required: {
                                    value: true,
                                    message: t('validation:required'),
                                },
                                maxLength: {
                                    value: 50,
                                    message: t('validation:maxlength'),
                                },
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                    message: t('validation:invalid-email'),
                                },
                            })}
                        />
                        {errors.email && (
                            <span className="absolute -right-36 mt-4 top-8 mt-4 text-sm block px-3 text-center text-error-red">
                                <FontAwesomeIcon
                                    className="fa-lg"
                                    icon={faExclamationCircle}
                                    data-tip={errors.email.message}
                                    data-for="validation"
                                />
                            </span>
                        )}
                    </div>
                    <div className="PasswordInput relative border-b border-clay">
                        <label
                            htmlFor="password"
                            className="label leading-tighter text-black font-gr-book text-xs cursor-text inline-block mt-2"
                        >
                            {t('base:password-label')}
                        </label>
                        <input
                            className="relative input text-xs appearance-none w-full px-2 py-2 pb-1 focus focus:border-indigo-600 focus:outline-none active:outline-none active:border-indigo-600"
                            id="password-register"
                            type={showPassword ? 'password' : 'text'}
                            name="password"
                            onFocus={rebuildTooltip}
                            onChange={rebuildTooltip}
                            data-for="input"
                            data-tip={t('base:password-tooltip')}
                            ref={register({
                                required: {
                                    value: true,
                                    message: t('validation:required'),
                                },
                                minLength: {
                                    value: 8,
                                    message: t('validation:min-length'),
                                },
                                maxLength: {
                                    value: 20,
                                    message: t('validation:max-length'),
                                },
                                pattern: {
                                    value: /^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?=.*[@$!_])(?!.*\s).{1,}$/g,
                                    message: t('validation:password-criteria'),
                                },
                            })}
                        />
                        <span className="absolute top-18 text-md right-10 cursor-pointer" onClick={onClickEye}>
                            {eye ? (
                                <FontAwesomeIcon className="fa-lg" icon={faEye} />
                            ) : (
                                <FontAwesomeIcon className="fa-lg" icon={faEyeSlash} />
                            )}
                        </span>
                        {errors.password && (
                            <span className="absolute -right-36 mt-4 top-8 text-sm block px-3 text-center text-error-red">
                                <FontAwesomeIcon
                                    className="fa-lg"
                                    icon={faExclamationCircle}
                                    data-tip={errors.password.message}
                                    data-for="validation"
                                />
                            </span>
                        )}
                    </div>
                    <div className="ConfirmPasswordInput relative border-b border-clay">
                        <label
                            htmlFor="confirm-password"
                            className="label leading-tighter text-black font-gr-book text-xs cursor-text inline-block mt-2"
                        >
                            {t('base:confirm-password-label')}
                        </label>
                        <input
                            className="relative input text-xs appearance-none w-full px-2 py-2 pb-1 focus focus:border-indigo-600 focus:outline-none active:outline-none active:border-indigo-600"
                            id="confirm-password-register"
                            type="password"
                            name="confirmPassword"
                            onFocus={rebuildTooltip}
                            onChange={rebuildTooltip}
                            ref={register({
                                required: {
                                    value: true,
                                    message: t('validation:required'),
                                },
                                validate: value =>
                                    value === watch('password') || t('validation:not-match-password').toString(),
                            })}
                        />
                        {errors.confirmPassword && (
                            <span className="absolute -right-36 mt-4 top-8 text-sm block px-3 text-center text-error-red">
                                <FontAwesomeIcon
                                    className="fa-lg"
                                    icon={faExclamationCircle}
                                    data-tip={errors.confirmPassword.message}
                                    data-for="validation"
                                />
                            </span>
                        )}
                    </div>
                    <div className="FirstNameInput relative border-b border-clay">
                        <label
                            htmlFor="firstName"
                            className="label leading-tighter text-black font-gr-book text-xs cursor-text inline-block mt-2"
                        >
                            {t('base:first-name-label').toString()}
                        </label>
                        <input
                            className="relative input text-xs appearance-none w-full px-2 py-2 pb-1 focus focus:border-indigo-600 focus:outline-none active:outline-none active:border-indigo-600"
                            id="first-name-register"
                            type="text"
                            name="firstName"
                            onFocus={rebuildTooltip}
                            onChange={rebuildTooltip}
                            ref={register({
                                required: {
                                    value: true,
                                    message: t('validation:required'),
                                },
                                minLength: {
                                    value: 2,
                                    message: t('validation:min-length'),
                                },
                                maxLength: {
                                    value: 30,
                                    message: t('validation:max-length'),
                                },
                                pattern: {
                                    value: /^[a-zA-Z ]+$/,
                                    message: t('validation:alpha-with-space'),
                                },
                            })}
                        />
                        {errors.firstName && (
                            <span className="absolute -right-36 mt-4 top-8 text-sm block px-3 text-center text-error-red">
                                <FontAwesomeIcon
                                    className="fa-lg"
                                    icon={faExclamationCircle}
                                    data-tip={errors.firstName.message}
                                    data-for="validation"
                                />
                            </span>
                        )}
                    </div>
                    <div className="LastNameInput relative border-b border-clay">
                        <label
                            htmlFor="lastname"
                            className="label leading-tighter text-black font-gr-book text-xs cursor-text inline-block mt-2"
                        >
                            {t('base:last-name-label')}
                        </label>
                        <input
                            className="relative input text-xs appearance-none w-full px-2 py-2 pb-1 focus focus:border-indigo-600 focus:outline-none active:outline-none active:border-indigo-600"
                            id="last-name-register"
                            type="text"
                            name="lastName"
                            onFocus={rebuildTooltip}
                            onChange={rebuildTooltip}
                            ref={register({
                                required: {
                                    value: true,
                                    message: t('validation:required'),
                                },
                                minLength: {
                                    value: 2,
                                    message: t('validation:min-length'),
                                },
                                maxLength: {
                                    value: 30,
                                    message: t('validation:max-length'),
                                },
                                pattern: {
                                    value: /^[a-zA-Z ]+$/,
                                    message: t('validation:alpha-with-space'),
                                },
                            })}
                        />
                        {errors.lastName && (
                            <span className="absolute -right-36 mt-4 top-8 text-sm block px-3 text-center text-error-red">
                                <FontAwesomeIcon
                                    className="fa-lg"
                                    icon={faExclamationCircle}
                                    data-tip={errors.lastName.message}
                                    data-for="validation"
                                />
                            </span>
                        )}
                    </div>
                </div>
                <div className="relative w-full h-4 border-r border-mini-boon border-solid">&nbsp;</div>
                <div className="relative w-full flex flex-no-wrap justify-end h-8">
                    <BoonButton
                        addtlContainerClasses="SubmitButton"
                        style="dark"
                        height="h-8"
                        width="w-22"
                        type="submit"
                        onClick={onClickSubmit}
                        disabled={disabled}
                        font="leading-8 text-white uppercase font-gr-light tracking-widest font-hairline text-xxs"
                    >
                        {t('base:signup-submit-label')}
                    </BoonButton>
                    <BoonLine lineColor="mini-boon" side="right" width="w-6" height="h-8" />
                </div>
            </div>
        </form>
    );
};

export default RegisterForm;
